/**
 * Client-side error reporter.
 *
 * 브라우저 에러(React 렌더 크래시, window.onerror, unhandledrejection, Apollo
 * errorLink, 수동 capture)를 단일 진입점에서 수집해 백엔드 GraphQL 뮤테이션으로 전송.
 *
 * 경로 분기:
 * - **로그인 상태** (access token 존재): `reportClientErrors` 뮤테이션 호출 +
 *   `Authorization: Bearer <token>` 헤더. 서버가 토큰을 검증해 user 컨텍스트를
 *   권위 있게 기록.
 * - **로그인 전** (토큰 없음): `reportAnonymousClientErrors` 뮤테이션 호출.
 *   user 정보 없이 익명 기록.
 *
 * 핵심 설계 원칙:
 * - Apollo Client **미사용**. Apollo 자체가 에러 소스이며 순환 장애 방지 위해
 *   raw fetch + sendBeacon 만 사용.
 * - `capture()`는 동기 반환 — `{ errorId }`를 즉시 받아 에러 UI에 바로 노출.
 * - 서버가 권위적으로 아는 필드(env, userAgent, userId/merchantId/role)는 **전송 X**.
 *   서버가 NODE_ENV, HTTP 헤더, 검증된 JWT 세션에서 직접 추출한다.
 * - 리포터 내부 에러는 절대 사용자 코드로 전파하지 않음.
 */

import { maskDeep, maskString, sanitizeUrl, generateErrorId } from './pii-mask'
import { getAccessToken } from './auth'

/**
 * 서버 GraphQL `ClientErrorSource` enum의 스키마 값과 반드시 일치.
 * GraphQL enum은 TS enum의 KEY를 와이어 심볼로 쓰므로 SCREAMING_SNAKE_CASE로 송신한다.
 */
export type ErrorSource =
  | 'REACT_RENDER'
  | 'WINDOW_ERROR'
  | 'UNHANDLED_REJECTION'
  | 'GRAPHQL'
  | 'NETWORK'
  | 'MANUAL'

export interface ErrorContext {
  source: ErrorSource
  componentStack?: string
  operationName?: string
  errorCode?: string
  httpStatus?: number
  extra?: Record<string, unknown>
}

export interface CapturedError {
  errorId: string
  capturedAt: string
}

export type ClientStage = 'development' | 'qa' | 'staging' | 'production'

interface ErrorReporterConfig {
  endpoint: string // GraphQL endpoint URL
  enabled: boolean
  stage: ClientStage // 클라이언트 내부용 — 샘플링 동작 분기에만 사용, 서버로 안 보냄
  release: string
  appName?: string
}

/**
 * 서버가 받는 `ClientErrorInput` 과 정확히 대응되는 필드만 포함.
 * env/userAgent/user 관련 필드는 서버가 스스로 결정하므로 제외.
 */
interface ErrorPayload {
  errorId: string
  release: string
  capturedAt: string
  source: ErrorSource
  name: string
  message: string
  stack?: string
  operationName?: string
  errorCode?: string
  httpStatus?: number
  url: string
  componentStack?: string
  extra?: Record<string, unknown>
}

const AUTHED_MUTATION = `mutation ReportClientErrors($input: ReportClientErrorsInput!) {
  reportClientErrors(input: $input) { accepted }
}`
const ANON_MUTATION = `mutation ReportAnonymousClientErrors($input: ReportClientErrorsInput!) {
  reportAnonymousClientErrors(input: $input) { accepted }
}`

const BATCH_FLUSH_MS = 500
const BATCH_MAX_ITEMS = 10
const BATCH_MAX_BYTES = 64 * 1024
const DEDUPE_WINDOW_MS = 5 * 60 * 1000
const DEDUPE_FLUSH_EVERY = 10
const DEDUPE_MAP_PRUNE_THRESHOLD = 500
const SAMPLE_MINUTE_WINDOW_MS = 60 * 1000
const SAMPLE_THRESHOLD_HIGH = 60
const SAMPLE_THRESHOLD_MED = 30
const TICK_MAX_CAPTURES = 3
const STACK_MAX_LINES = 20
const COMPONENT_STACK_MAX_LINES = 40

class ErrorReporter {
  private config: ErrorReporterConfig | null = null
  private queue: ErrorPayload[] = []
  private flushTimer: ReturnType<typeof setTimeout> | null = null
  private sending = false
  // visibilitychange(hidden) + pagehide가 거의 동시에 발생할 수 있어 flush 중복 방어.
  // 실제 종료 시점까지 true 로 유지되므로 isUnload 경로에도 단일 진입 보장.
  private unloadFlushed = false

  private dedupe = new Map<
    string,
    { count: number; firstSeen: number; lastFlushCount: number }
  >()

  private sampleWindowStart = Date.now()
  private sampleWindowCount = 0

  private currentTickCaptures = 0
  private tickResetScheduled = false

  init(config: ErrorReporterConfig): void {
    this.config = config
  }

  /**
   * 에러 캡처. 동기 반환 — `{ errorId, capturedAt }` 을 받아 UI에 즉시 표시 가능.
   * 실제 전송은 비동기 배치.
   */
  capture(error: unknown, context: Partial<ErrorContext> = {}): CapturedError {
    const errorId = generateErrorId()
    const capturedAt = new Date().toISOString()

    try {
      // 리포터 비활성(enabled=false) 환경에서는 payload 빌드·큐 적재 자체를 건너뛴다.
      // shouldSample 내부 체크만으로는 전송만 막힐 뿐 메모리 낭비가 발생해 조기 반환.
      if (!this.config?.enabled) return { errorId, capturedAt }
      if (!this.shouldSample()) return { errorId, capturedAt }
      if (!this.allowThisTick()) return { errorId, capturedAt }

      const payload = this.buildPayload(error, context, errorId, capturedAt)
      const dedupeKey = this.dedupeKeyOf(payload)
      if (!this.shouldAcceptByDedupe(dedupeKey)) return { errorId, capturedAt }

      this.enqueue(payload)
    } catch (innerError) {
      // 리포터 내부 에러는 절대 재throw하지 않는다 — 사용자 플로우 보호
      // eslint-disable-next-line no-console
      console.warn('[errorReporter] internal error (swallowed):', innerError)
    }

    return { errorId, capturedAt }
  }

  /** 큐를 즉시 비운다. pagehide/visibilitychange(hidden) 시 호출. */
  async flush(): Promise<void> {
    await this.send(true)
  }

  // ---------------------------------------------------------------------------
  // Private
  // ---------------------------------------------------------------------------

  private buildPayload(
    error: unknown,
    context: Partial<ErrorContext>,
    errorId: string,
    capturedAt: string
  ): ErrorPayload {
    const { name, message, stack } = this.normalizeError(error)
    const release = this.config?.release ?? 'unknown'

    return {
      errorId,
      release,
      capturedAt,
      source: context.source ?? 'MANUAL',
      name: maskString(name).slice(0, 128) || 'Error',
      message: maskString(message).slice(0, 2000),
      stack: stack
        ? this.truncateLines(maskString(stack), STACK_MAX_LINES).slice(0, 8000)
        : undefined,
      operationName: context.operationName?.slice(0, 128),
      errorCode: context.errorCode?.slice(0, 64),
      httpStatus: context.httpStatus,
      url:
        typeof window !== 'undefined'
          ? sanitizeUrl(
              window.location.pathname + window.location.search
            ).slice(0, 1024)
          : '',
      componentStack: context.componentStack
        ? this.truncateLines(
            maskString(context.componentStack),
            COMPONENT_STACK_MAX_LINES
          ).slice(0, 4000)
        : undefined,
      extra: context.extra
        ? (maskDeep(context.extra) as Record<string, unknown>)
        : undefined,
    }
  }

  private normalizeError(error: unknown): {
    name: string
    message: string
    stack?: string
  } {
    if (error instanceof Error) {
      return { name: error.name, message: error.message, stack: error.stack }
    }
    if (typeof error === 'string') {
      return { name: 'Error', message: error }
    }
    try {
      return { name: 'Error', message: JSON.stringify(error) }
    } catch {
      return { name: 'Error', message: String(error) }
    }
  }

  private truncateLines(value: string, maxLines: number): string {
    const lines = value.split('\n')
    if (lines.length <= maxLines) return value
    return (
      lines.slice(0, maxLines).join('\n') +
      `\n… ${lines.length - maxLines} more`
    )
  }

  private dedupeKeyOf(p: ErrorPayload): string {
    const stackHead = (p.stack ?? '').split('\n').slice(0, 3).join('|')
    return `${p.name}::${p.message}::${p.operationName ?? ''}::${stackHead}`
  }

  private shouldAcceptByDedupe(key: string): boolean {
    const now = Date.now()
    this.pruneDedupeIfNeeded(now)

    const entry = this.dedupe.get(key)
    if (!entry) {
      this.dedupe.set(key, { count: 1, firstSeen: now, lastFlushCount: 0 })
      return true
    }

    if (now - entry.firstSeen > DEDUPE_WINDOW_MS) {
      this.dedupe.set(key, { count: 1, firstSeen: now, lastFlushCount: 0 })
      return true
    }

    entry.count++
    if (entry.count - entry.lastFlushCount >= DEDUPE_FLUSH_EVERY) {
      entry.lastFlushCount = entry.count
      return true
    }
    return false
  }

  private pruneDedupeIfNeeded(now: number): void {
    if (this.dedupe.size < DEDUPE_MAP_PRUNE_THRESHOLD) return
    this.dedupe.forEach((entry, key) => {
      if (now - entry.firstSeen > DEDUPE_WINDOW_MS) {
        this.dedupe.delete(key)
      }
    })
  }

  private shouldSample(): boolean {
    const cfg = this.config
    if (!cfg) return false
    if (cfg.stage === 'development') return false // 로컬은 콘솔로 충분

    const now = Date.now()
    if (now - this.sampleWindowStart > SAMPLE_MINUTE_WINDOW_MS) {
      this.sampleWindowStart = now
      this.sampleWindowCount = 0
    }
    this.sampleWindowCount++

    if (this.sampleWindowCount > SAMPLE_THRESHOLD_HIGH) {
      return Math.random() < 0.1
    }
    if (this.sampleWindowCount > SAMPLE_THRESHOLD_MED) {
      return Math.random() < 0.5
    }
    return true
  }

  private allowThisTick(): boolean {
    if (this.currentTickCaptures >= TICK_MAX_CAPTURES) return false
    this.currentTickCaptures++
    if (!this.tickResetScheduled) {
      this.tickResetScheduled = true
      setTimeout(() => {
        this.currentTickCaptures = 0
        this.tickResetScheduled = false
      }, 0)
    }
    return true
  }

  private enqueue(payload: ErrorPayload): void {
    // 오버플로우 시 신규 에러를 버린다(FIFO 유지). 연쇄 에러에서 원인이 되는 첫 번째
    // 에러가 디버깅 가치가 가장 높으므로 오래된 항목을 보존.
    if (this.queue.length >= BATCH_MAX_ITEMS) return

    this.queue.push(payload)

    if (this.flushTimer) return
    this.flushTimer = setTimeout(() => {
      this.flushTimer = null
      void this.send(false)
    }, BATCH_FLUSH_MS)
  }

  private async send(isUnload: boolean): Promise<void> {
    const cfg = this.config
    if (!cfg || !cfg.enabled || this.queue.length === 0) return
    if (this.sending && !isUnload) return
    // isUnload 경로 재진입 방어 — visibilitychange + pagehide 중복 호출 방지
    if (isUnload) {
      if (this.unloadFlushed) return
      this.unloadFlushed = true
    }

    const batch = this.queue.splice(0, BATCH_MAX_ITEMS)
    this.sending = true

    try {
      // 토큰 유무로 뮤테이션 분기 — 로그인 유저는 서버가 토큰에서 user를 권위 있게 추출,
      // 익명은 user 필드 없이 기록.
      const token = getAccessToken()
      const mutationText = token ? AUTHED_MUTATION : ANON_MUTATION
      const operationName = token
        ? 'ReportClientErrors'
        : 'ReportAnonymousClientErrors'

      const body = JSON.stringify({
        query: mutationText,
        variables: { input: { errors: batch } },
        operationName,
      })

      if (body.length > BATCH_MAX_BYTES) {
        const halfCount = Math.max(1, Math.floor(batch.length / 2))
        const half = batch.slice(0, halfCount)
        const remainder = batch.slice(halfCount)
        if (remainder.length > 0) {
          this.queue.unshift(...remainder)
          if (!this.flushTimer) {
            this.flushTimer = setTimeout(() => {
              this.flushTimer = null
              void this.send(false)
            }, BATCH_FLUSH_MS)
          }
        }
        const halfBody = JSON.stringify({
          query: mutationText,
          variables: { input: { errors: half } },
          operationName,
        })
        await this.dispatch(
          cfg.endpoint,
          halfBody,
          isUnload,
          cfg.appName,
          token
        )
        return
      }

      await this.dispatch(cfg.endpoint, body, isUnload, cfg.appName, token)
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[errorReporter] send failed:', err)
    } finally {
      this.sending = false
    }
  }

  private async dispatch(
    endpoint: string,
    body: string,
    isUnload: boolean,
    appName: string | undefined,
    token: string | null
  ): Promise<void> {
    const headers: Record<string, string> = {
      'content-type': 'application/json',
    }
    if (appName) headers['x-app'] = appName
    if (token) headers['authorization'] = `Bearer ${token}`

    // sendBeacon은 커스텀 헤더를 지원하지 않으므로 인증이 있으면 keepalive fetch 사용.
    // 익명 경로(토큰 없음)에서만 beacon 사용 가능.
    const canUseBeacon =
      isUnload &&
      !token &&
      typeof navigator !== 'undefined' &&
      typeof navigator.sendBeacon === 'function'

    if (canUseBeacon) {
      const blob = new Blob([body], { type: 'application/json' })
      const ok = navigator.sendBeacon(endpoint, blob)
      if (ok) return
    }

    await fetch(endpoint, {
      method: 'POST',
      headers,
      body,
      keepalive: true,
      credentials: 'omit', // 쿠키 불필요 — 인증은 헤더로만
    })
  }
}

export const errorReporter = new ErrorReporter()
